/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { keyframes } from "@emotion/react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { type ThemeType, zIndices } from "../../../../emotion";

// Animation keyframes
const fadeIn = keyframes({
  "0%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const fadeOut = keyframes({
  "0%": { opacity: 1 },
  "100%": { opacity: 0 },
});

const zoomIn = keyframes({
  "0%": { transform: "scale(0.95)" },
  "100%": { transform: "scale(1)" },
});

const zoomOut = keyframes({
  "0%": { transform: "scale(1)" },
  "100%": { transform: "scale(0.95)" },
});

const slideInFromTop = keyframes({
  "0%": { transform: "translateY(-8px)" },
  "100%": { transform: "translateY(0)" },
});

const slideInFromBottom = keyframes({
  "0%": { transform: "translateY(8px)" },
  "100%": { transform: "translateY(0)" },
});

const slideInFromLeft = keyframes({
  "0%": { transform: "translateX(-8px)" },
  "100%": { transform: "translateX(0)" },
});

const slideInFromRight = keyframes({
  "0%": { transform: "translateX(8px)" },
  "100%": { transform: "translateX(0)" },
});

// Styles
const tooltipContentStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.primary,
  color: theme.semanticColors.background,
  zIndex: zIndices.tooltip,
  width: "fit-content",
  transformOrigin: "var(--radix-tooltip-content-transform-origin)",
  borderRadius: "0.375rem",
  padding: "0.375rem 0.75rem",
  fontSize: "0.75rem",
  lineHeight: "1rem",
  textWrap: "balance" as const,

  "&[data-state='open']": {
    animation: `${fadeIn} 150ms ease-out, ${zoomIn} 150ms ease-out`,
  },
  "&[data-state='closed']": {
    animation: `${fadeOut} 150ms ease-in, ${zoomOut} 150ms ease-in`,
  },

  "&[data-side='bottom']": {
    animation: `${fadeIn} 150ms ease-out, ${zoomIn} 150ms ease-out, ${slideInFromTop} 150ms ease-out`,
  },
  "&[data-side='left']": {
    animation: `${fadeIn} 150ms ease-out, ${zoomIn} 150ms ease-out, ${slideInFromRight} 150ms ease-out`,
  },
  "&[data-side='right']": {
    animation: `${fadeIn} 150ms ease-out, ${zoomIn} 150ms ease-out, ${slideInFromLeft} 150ms ease-out`,
  },
  "&[data-side='top']": {
    animation: `${fadeIn} 150ms ease-out, ${zoomIn} 150ms ease-out, ${slideInFromBottom} 150ms ease-out`,
  },
});

const arrowStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.primary,
  fill: theme.semanticColors.primary,
  zIndex: 50,
  width: "0.625rem",
  height: "0.625rem",
  transform: "translateY(calc(-50% - 2px)) rotate(45deg)",
  borderRadius: "2px",
});

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        css={tooltipContentStyle}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow css={arrowStyle} />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
